import { useEffect, useState } from "react";
import {
  ArrowRight,
  Banknote,
  BriefcaseBusiness,
  Building2,
  Check,
  ClipboardList,
  Copy,
  Download,
  Headphones,
  History,
  KeyRound,
  LockKeyhole,
  LogOut,
  Mail,
  MapPin,
  MapPinned,
  PackageCheck,
  PackageSearch,
  Plus,
  Receipt,
  ShieldCheck,
  Trash2,
  Truck,
  UserRound,
  UsersRound,
  Wallet,
} from "lucide-react";
import { SiteFooter, SiteHeader } from "./components/SiteChrome.jsx";
import { apiRequest } from "./services/apiClient.js";
import { setPageMeta } from "./services/seo.js";

const portalNames = {
  employee: "Employee Portal",
  driver: "Driver Portal",
  customer: "Customer Portal",
};
const Shell = ({ children }) => (
  <div className="multi-shell">
    <SiteHeader />
    <main id="page-content">{children}</main>
    <SiteFooter />
  </div>
);

export function LoginPage() {
  const portal =
    new URLSearchParams(window.location.search).get("portal") || "employee";
  const [form, setForm] = useState({
    email: "",
    password: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [requestForm, setRequestForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    organization: "",
    message: "",
  });
  const [status, setStatus] = useState({
    loading: false,
    error: "",
    success: "",
    mustChange: false,
    name: "",
    mode: "login",
  });
  setPageMeta({
    title: `${portalNames[portal] || "Portal"} Login | Just Go Logistics`,
    description:
      "Secure account login for approved Just Go Logistics portal users.",
    path: "/login",
  });

  const update = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  const updateRequest = (event) =>
    setRequestForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  async function login(event) {
    event.preventDefault();
    setStatus((current) => ({ ...current, loading: true, error: "" }));
    try {
      const result = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: form.email, password: form.password }),
      });
      if (result.requiresPasswordChange)
        return setStatus({
          loading: false,
          error: "",
          mustChange: true,
          name: result.name,
        });
      window.location.assign("/portal");
    } catch (error) {
      setStatus((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function changePassword(event) {
    event.preventDefault();
    setStatus((current) => ({ ...current, loading: true, error: "" }));
    try {
      await apiRequest("/auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          password: form.newPassword,
          confirmPassword: form.confirmPassword,
        }),
      });
      window.location.assign("/portal");
    } catch (error) {
      setStatus((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function requestAccess(event) {
    event.preventDefault();
    setStatus((current) => ({
      ...current,
      loading: true,
      error: "",
      success: "",
    }));
    try {
      const result = await apiRequest("/auth/request-access", {
        method: "POST",
        body: JSON.stringify({ ...requestForm, requestedRole: portal }),
      });
      setRequestForm({
        fullName: "",
        email: "",
        phone: "",
        organization: "",
        message: "",
      });
      setStatus((current) => ({
        ...current,
        loading: false,
        success: result.message,
      }));
    } catch (error) {
      setStatus((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }

  return (
    <Shell>
      <section className="portal-auth-page">
        <div className="portal-auth-card">
          <span className="portal-auth-icon">
            {status.mustChange ? (
              <KeyRound size={30} />
            ) : status.mode === "request" ? (
              <UserRound size={30} />
            ) : (
              <LockKeyhole size={30} />
            )}
          </span>
          <p className="eyebrow">Secure account access</p>
          <h1>
            {status.mustChange
              ? "Create a new password"
              : portalNames[portal] || "Portal Login"}
          </h1>
          {!status.mustChange && (
            <div className="portal-auth-tabs">
              <button
                type="button"
                className={status.mode === "login" ? "active" : ""}
                onClick={() =>
                  setStatus((current) => ({
                    ...current,
                    mode: "login",
                    error: "",
                    success: "",
                  }))
                }
              >
                Sign in
              </button>
              <button
                type="button"
                className={status.mode === "request" ? "active" : ""}
                onClick={() =>
                  setStatus((current) => ({
                    ...current,
                    mode: "request",
                    error: "",
                    success: "",
                  }))
                }
              >
                Request access
              </button>
            </div>
          )}
          <p>
            {status.mustChange
              ? `${status.name}, your temporary password must be replaced before you can continue.`
              : status.mode === "request"
                ? `Submit a request for ${portalNames[portal] || "portal"} access. An administrator must approve your account before you can sign in.`
                : "Enter the credentials issued for your approved account."}
          </p>
          {status.error && (
            <div className="portal-auth-error" role="alert">
              {status.error}
            </div>
          )}
          {status.success && (
            <div className="portal-admin-notice" role="status">
              <Check size={18} />
              <span>{status.success}</span>
            </div>
          )}
          {status.mustChange ? (
            <form onSubmit={changePassword}>
              <label>
                New password
                <input
                  type="password"
                  name="newPassword"
                  value={form.newPassword}
                  onChange={update}
                  autoComplete="new-password"
                  minLength="12"
                  required
                />
              </label>
              <small>
                At least 12 characters with uppercase, lowercase, number, and
                symbol.
              </small>
              <label>
                Confirm new password
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={update}
                  autoComplete="new-password"
                  minLength="12"
                  required
                />
              </label>
              <button
                className="button button-primary"
                disabled={status.loading}
              >
                {status.loading ? "Updating…" : "Update password and continue"}{" "}
                <ArrowRight size={17} />
              </button>
            </form>
          ) : status.mode === "request" ? (
            <form onSubmit={requestAccess}>
              <label>
                Full name
                <input
                  name="fullName"
                  value={requestForm.fullName}
                  onChange={updateRequest}
                  maxLength="160"
                  required
                />
              </label>
              <label>
                Email address
                <input
                  type="email"
                  name="email"
                  value={requestForm.email}
                  onChange={updateRequest}
                  required
                />
              </label>
              <label>
                Phone number <small>Optional</small>
                <input
                  type="tel"
                  name="phone"
                  value={requestForm.phone}
                  onChange={updateRequest}
                  maxLength="40"
                />
              </label>
              <label>
                Company or organization <small>Optional</small>
                <input
                  name="organization"
                  value={requestForm.organization}
                  onChange={updateRequest}
                  maxLength="160"
                />
              </label>
              <label>
                Reason for requesting access <small>Optional</small>
                <textarea
                  name="message"
                  value={requestForm.message}
                  onChange={updateRequest}
                  maxLength="1000"
                  rows="4"
                />
              </label>
              <button
                className="button button-primary"
                disabled={status.loading}
              >
                {status.loading ? "Submitting…" : "Submit request for approval"}{" "}
                <ArrowRight size={17} />
              </button>
            </form>
          ) : (
            <form onSubmit={login}>
              <label>
                Email address
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={update}
                  autoComplete="username"
                  required
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={update}
                  autoComplete="current-password"
                  required
                />
              </label>
              <button
                className="button button-primary"
                disabled={status.loading}
              >
                {status.loading ? "Signing in…" : "Login"}{" "}
                <ArrowRight size={17} />
              </button>
            </form>
          )}
          <div className="portal-auth-security">
            <ShieldCheck size={18} />
            <span>
              Private system. Access requests and account activity may be logged
              and reviewed.
            </span>
          </div>
        </div>
      </section>
    </Shell>
  );
}

function OperationsWorkspace() {
  const [data, setData] = useState({
    orders: [],
    drivers: [],
    statuses: [],
    partners: [],
    services: [],
    profile: null,
    loading: true,
    error: "",
    notice: "",
  });
  const [partner, setPartner] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    services: "",
    notes: "",
  });
  const [service, setService] = useState({ serviceName: "", description: "" });
  const [profile, setProfile] = useState({
    jobTitle: "",
    department: "",
    phone: "",
    workLocation: "",
    responsibilities: "",
    serviceSpecialties: "",
  });
  async function loadOperations() {
    try {
      const [orders, drivers, partners, services, profileResult] =
        await Promise.all([
          apiRequest("/operations/orders"),
          apiRequest("/operations/drivers"),
          apiRequest("/operations/partners"),
          apiRequest("/operations/services"),
          apiRequest("/operations/profile"),
        ]);
      setData((current) => ({
        ...current,
        orders: orders.orders,
        statuses: orders.statuses,
        drivers: drivers.drivers,
        partners: partners.partners,
        services: services.services,
        profile: profileResult.profile,
        loading: false,
        error: "",
      }));
      const value = profileResult.profile || {};
      setProfile({
        jobTitle: value.job_title || "",
        department: value.department || "",
        phone: value.phone || "",
        workLocation: value.work_location || "",
        responsibilities: value.responsibilities || "",
        serviceSpecialties: value.service_specialties || "",
      });
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  useEffect(() => {
    loadOperations();
  }, []);
  async function updateOrder(order, changes) {
    setData((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
    }));
    try {
      await apiRequest(
        `/operations/orders/${encodeURIComponent(order.request_number)}`,
        { method: "PATCH", body: JSON.stringify(changes) },
      );
      await loadOperations();
      setData((current) => ({
        ...current,
        loading: false,
        notice: `Order ${order.request_number} was updated.`,
      }));
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function addPartner(event) {
    event.preventDefault();
    try {
      await apiRequest("/operations/partners", {
        method: "POST",
        body: JSON.stringify(partner),
      });
      setPartner({
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
        services: "",
        notes: "",
      });
      await loadOperations();
      setData((current) => ({
        ...current,
        notice: "Logistics company added.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function addService(event) {
    event.preventDefault();
    try {
      await apiRequest("/operations/services", {
        method: "POST",
        body: JSON.stringify(service),
      });
      setService({ serviceName: "", description: "" });
      await loadOperations();
      setData((current) => ({
        ...current,
        notice: "Service added to the operations catalog.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function saveProfile(event) {
    event.preventDefault();
    try {
      await apiRequest("/operations/profile", {
        method: "PATCH",
        body: JSON.stringify(profile),
      });
      setData((current) => ({
        ...current,
        notice: "Your work profile was updated.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  if (data.loading && !data.orders.length)
    return (
      <section className="operations-workspace">
        <p>Loading operations workspace…</p>
      </section>
    );
  return (
    <section className="operations-workspace">
      <header>
        <p className="eyebrow">Employee operations</p>
        <h2>Orders, assignments, customers and services</h2>
        <p>
          Manage active delivery work, customer details, payment records,
          drivers, logistics partners, services, and your professional profile.
        </p>
      </header>
      {data.error && (
        <div className="portal-auth-error" role="alert">
          {data.error}
        </div>
      )}
      {data.notice && (
        <div className="portal-admin-notice">
          <Check size={18} />
          <span>{data.notice}</span>
        </div>
      )}
      <div className="operations-section">
        <div className="operations-title">
          <ClipboardList size={23} />
          <div>
            <h3>All orders</h3>
            <span>{data.orders.length} records</span>
          </div>
        </div>
        <div className="operations-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order and route</th>
                <th>Customer</th>
                <th>Receipt</th>
                <th>Order status</th>
                <th>Assigned driver</th>
                <th>Delivery status and proof</th>
              </tr>
            </thead>
            <tbody>
              {data.orders.map((order) => (
                <tr key={order.request_number}>
                  <td>
                    <strong>{order.request_number}</strong>
                    <span>{order.tracking_number || "Tracking pending"}</span>
                    <small>
                      {order.pickup_city}, {order.pickup_state} →{" "}
                      {order.delivery_city}, {order.delivery_state}
                    </small>
                    <small>{order.service_type}</small>
                  </td>
                  <td>
                    <strong>
                      {order.first_name} {order.last_name}
                    </strong>
                    <span>{order.company_name || order.customer_type}</span>
                    <a href={`mailto:${order.email}`}>{order.email}</a>
                    <a href={`tel:${order.phone}`}>{order.phone}</a>
                  </td>
                  <td>
                    <strong>{order.payment_status}</strong>
                    <span>
                      $
                      {Number(
                        order.amount_paid || order.estimated_price || 0,
                      ).toFixed(2)}
                    </span>
                    <small>
                      {order.paid_at
                        ? new Date(order.paid_at).toLocaleString()
                        : "Not paid"}
                    </small>
                    {order.stripe_payment_intent_id && (
                      <small>{order.stripe_payment_intent_id}</small>
                    )}
                  </td>
                  <td>
                    <select
                      value={order.status}
                      onChange={(event) =>
                        updateOrder(order, { status: event.target.value })
                      }
                      disabled={data.loading}
                    >
                      {data.statuses.map((status) => (
                        <option key={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={order.driver_user_id || ""}
                      onChange={(event) =>
                        updateOrder(order, {
                          driverUserId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      disabled={data.loading}
                    >
                      <option value="">Unassigned</option>
                      {data.drivers.map((driver) => (
                        <option key={driver.id} value={driver.id}>
                          {driver.full_name}
                        </option>
                      ))}
                    </select>
                    {order.driver_email && <small>{order.driver_email}</small>}
                  </td>
                  <td className="delivery-proof-cell">
                    <strong>{order.status.replaceAll("_", " ")}</strong>
                    <small>
                      {order.delivered_at
                        ? `Delivered ${new Date(order.delivered_at).toLocaleString()}`
                        : order.assigned_at
                          ? `Assigned ${new Date(order.assigned_at).toLocaleString()}`
                          : "Awaiting driver assignment"}
                    </small>
                    {order.proof_recipient && (
                      <small>Received by {order.proof_recipient}</small>
                    )}
                    {Boolean(order.has_proof_image) && (
                      <a
                        href={`/api/operations/orders/${encodeURIComponent(order.request_number)}/proof-image`}
                        target="_blank"
                        rel="noreferrer"
                        className="delivery-proof-link"
                      >
                        <img
                          src={`/api/operations/orders/${encodeURIComponent(order.request_number)}/proof-image`}
                          alt={`Proof of delivery for ${order.request_number}`}
                        />
                        <span>View delivery photo</span>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="operations-tools">
        <section>
          <div className="operations-title">
            <Building2 size={22} />
            <h3>Logistics companies</h3>
          </div>
          <div className="operations-records">
            {data.partners.map((item) => (
              <article key={item.id}>
                <strong>{item.company_name}</strong>
                <span>{item.services || "Services not specified"}</span>
                <small>
                  {item.contact_name || ""} {item.email || ""}{" "}
                  {item.phone || ""}
                </small>
              </article>
            ))}
          </div>
          <form onSubmit={addPartner}>
            <label>
              Company name
              <input
                value={partner.companyName}
                onChange={(event) =>
                  setPartner((current) => ({
                    ...current,
                    companyName: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Contact name
              <input
                value={partner.contactName}
                onChange={(event) =>
                  setPartner((current) => ({
                    ...current,
                    contactName: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Email
              <input
                type="email"
                value={partner.email}
                onChange={(event) =>
                  setPartner((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Phone
              <input
                value={partner.phone}
                onChange={(event) =>
                  setPartner((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Services
              <textarea
                value={partner.services}
                onChange={(event) =>
                  setPartner((current) => ({
                    ...current,
                    services: event.target.value,
                  }))
                }
              />
            </label>
            <button className="button button-dark">Add company</button>
          </form>
        </section>
        <section>
          <div className="operations-title">
            <Truck size={22} />
            <h3>Service catalog</h3>
          </div>
          <div className="operations-records">
            {data.services.map((item) => (
              <article key={item.id}>
                <strong>{item.service_name}</strong>
                <span>{item.description || "No description"}</span>
              </article>
            ))}
          </div>
          <form onSubmit={addService}>
            <label>
              Service name
              <input
                value={service.serviceName}
                onChange={(event) =>
                  setService((current) => ({
                    ...current,
                    serviceName: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Description
              <textarea
                value={service.description}
                onChange={(event) =>
                  setService((current) => ({
                    ...current,
                    description: event.target.value,
                  }))
                }
              />
            </label>
            <button className="button button-dark">Add service</button>
          </form>
        </section>
        <section>
          <div className="operations-title">
            <UserRound size={22} />
            <h3>My work profile</h3>
          </div>
          <form onSubmit={saveProfile}>
            <label>
              Job title
              <input
                value={profile.jobTitle}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    jobTitle: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Department
              <input
                value={profile.department}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    department: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Work phone
              <input
                value={profile.phone}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    phone: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Work location
              <input
                value={profile.workLocation}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    workLocation: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Responsibilities
              <textarea
                value={profile.responsibilities}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    responsibilities: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Service specialties
              <textarea
                value={profile.serviceSpecialties}
                onChange={(event) =>
                  setProfile((current) => ({
                    ...current,
                    serviceSpecialties: event.target.value,
                  }))
                }
              />
            </label>
            <button className="button button-dark">Save profile</button>
          </form>
        </section>
      </div>
    </section>
  );
}

function DriverWorkspace() {
  const [data, setData] = useState({
    activeOrders: [],
    orderHistory: [],
    summary: {},
    paymentSettings: null,
    withdrawals: [],
    loading: true,
    error: "",
    notice: "",
  });
  const [settings, setSettings] = useState({
    payoutMethod: "bank_transfer",
    destinationLabel: "",
    accountLast4: "",
    payoutSchedule: "weekly",
    minimumPayout: 25,
    withdrawalsEnabled: false,
  });
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [proof, setProof] = useState({
    requestNumber: "",
    recipientName: "",
    confirmationReference: "",
    deliveryNotes: "",
    proofImage: null,
  });
  async function loadDashboard() {
    try {
      const result = await apiRequest("/driver/dashboard");
      setData((current) => ({
        ...current,
        ...result,
        loading: false,
        error: "",
      }));
      const value = result.paymentSettings;
      if (value)
        setSettings({
          payoutMethod: value.payout_method,
          destinationLabel: value.destination_label || "",
          accountLast4: value.account_last4 || "",
          payoutSchedule: value.payout_schedule,
          minimumPayout: Number(value.minimum_payout),
          withdrawalsEnabled: Boolean(value.withdrawals_enabled),
        });
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  useEffect(() => {
    loadDashboard();
  }, []);
  async function updateStatus(requestNumber, status) {
    try {
      await apiRequest(
        `/driver/orders/${encodeURIComponent(requestNumber)}/status`,
        { method: "PATCH", body: JSON.stringify({ status }) },
      );
      await loadDashboard();
      setData((current) => ({
        ...current,
        notice: `Order ${requestNumber} updated to ${status.replaceAll("_", " ")}.`,
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function submitProof(event) {
    event.preventDefault();
    try {
      const formData = new FormData();
      formData.append("recipientName", proof.recipientName);
      formData.append("confirmationReference", proof.confirmationReference);
      formData.append("deliveryNotes", proof.deliveryNotes);
      formData.append("proofImage", proof.proofImage);
      await apiRequest(
        `/driver/orders/${encodeURIComponent(proof.requestNumber)}/proof`,
        { method: "POST", body: formData },
      );
      setProof({
        requestNumber: "",
        recipientName: "",
        confirmationReference: "",
        deliveryNotes: "",
        proofImage: null,
      });
      await loadDashboard();
      setData((current) => ({
        ...current,
        notice: "Proof of delivery saved and the order was completed.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function saveSettings(event) {
    event.preventDefault();
    try {
      await apiRequest("/driver/payment-settings", {
        method: "PATCH",
        body: JSON.stringify(settings),
      });
      await loadDashboard();
      setData((current) => ({
        ...current,
        notice: "Withdrawal settings saved securely.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function requestWithdrawal(event) {
    event.preventDefault();
    try {
      await apiRequest("/driver/withdrawals", {
        method: "POST",
        body: JSON.stringify({ amount: Number(withdrawalAmount) }),
      });
      setWithdrawalAmount("");
      await loadDashboard();
      setData((current) => ({
        ...current,
        notice: "Withdrawal request submitted for review.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  if (data.loading)
    return (
      <section className="driver-workspace">
        <p>Loading driver workspace…</p>
      </section>
    );
  const money = (value) => `$${Number(value || 0).toFixed(2)}`;
  return (
    <section className="driver-workspace">
      <header>
        <p className="eyebrow">Driver operations</p>
        <h2>Rides, deliveries and earnings</h2>
        <p>
          Manage assigned work, update delivery progress, submit proof of
          delivery, review earnings, and request payouts.
        </p>
      </header>
      {data.error && (
        <div className="portal-auth-error" role="alert">
          {data.error}
        </div>
      )}
      {data.notice && (
        <div className="portal-admin-notice">
          <Check size={18} />
          <span>{data.notice}</span>
        </div>
      )}
      <div className="driver-summary">
        <article>
          <Truck />
          <span>Active rides</span>
          <strong>{data.activeOrders.length}</strong>
        </article>
        <article>
          <PackageCheck />
          <span>Completed orders</span>
          <strong>
            {
              data.orderHistory.filter((item) => item.status === "DELIVERED")
                .length
            }
          </strong>
        </article>
        <article>
          <Wallet />
          <span>Available balance</span>
          <strong>{money(data.summary.available_balance)}</strong>
        </article>
        <article>
          <Banknote />
          <span>Pending earnings</span>
          <strong>{money(data.summary.pending_earnings)}</strong>
        </article>
      </div>
      <section className="driver-panel">
        <div className="operations-title">
          <MapPin size={23} />
          <div>
            <h3>Assigned rides and orders</h3>
            <span>{data.activeOrders.length} active</span>
          </div>
        </div>
        {!data.activeOrders.length ? (
          <p className="driver-empty">No active orders are assigned to you.</p>
        ) : (
          <div className="driver-order-grid">
            {data.activeOrders.map((order) => (
              <article key={order.request_number}>
                <div className="driver-order-head">
                  <div>
                    <strong>{order.request_number}</strong>
                    <span>{order.status.replaceAll("_", " ")}</span>
                  </div>
                  <small>{order.tracking_number || "Tracking pending"}</small>
                </div>
                <div className="driver-route">
                  <p>
                    <b>Pickup</b>
                    {order.pickup_company}
                    <br />
                    {order.pickup_street}, {order.pickup_city},{" "}
                    {order.pickup_state} {order.pickup_zip}
                    <br />
                    {order.pickup_date} · {order.pickup_time}
                  </p>
                  <ArrowRight />
                  <p>
                    <b>Delivery</b>
                    {order.delivery_company}
                    <br />
                    {order.delivery_street}, {order.delivery_city},{" "}
                    {order.delivery_state} {order.delivery_zip}
                    <br />
                    {order.delivery_date} · {order.delivery_time}
                  </p>
                </div>
                <div className="driver-contact">
                  <span>
                    Pickup contact: {order.pickup_contact_name} ·{" "}
                    {order.pickup_contact_phone}
                  </span>
                  <span>
                    Recipient: {order.recipient_name} · {order.recipient_phone}
                  </span>
                  <span>
                    Customer payment: {order.payment_status} ·{" "}
                    {money(order.amount_paid)}
                  </span>
                  <span>
                    Driver pay:{" "}
                    {order.earning_status
                      ? `${money(order.driver_pay)} · ${order.earning_status}`
                      : "Pending dispatch calculation"}
                  </span>
                </div>
                <div className="driver-actions">
                  <button
                    type="button"
                    onClick={() =>
                      updateStatus(order.request_number, "PICKED_UP")
                    }
                  >
                    Picked up
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      updateStatus(order.request_number, "IN_TRANSIT")
                    }
                  >
                    In transit
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      updateStatus(order.request_number, "ON_HOLD")
                    }
                  >
                    On hold
                  </button>
                  <button
                    type="button"
                    className="complete"
                    onClick={() =>
                      setProof((current) => ({
                        ...current,
                        requestNumber: order.request_number,
                      }))
                    }
                  >
                    Complete delivery
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
      {proof.requestNumber && (
        <section className="driver-panel proof-panel">
          <div className="operations-title">
            <PackageCheck size={23} />
            <h3>Proof of delivery · {proof.requestNumber}</h3>
          </div>
          <form onSubmit={submitProof}>
            <label>
              Recipient or authorized receiver
              <input
                value={proof.recipientName}
                onChange={(event) =>
                  setProof((current) => ({
                    ...current,
                    recipientName: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Confirmation reference <small>Optional</small>
              <input
                value={proof.confirmationReference}
                onChange={(event) =>
                  setProof((current) => ({
                    ...current,
                    confirmationReference: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Delivery notes
              <textarea
                value={proof.deliveryNotes}
                onChange={(event) =>
                  setProof((current) => ({
                    ...current,
                    deliveryNotes: event.target.value,
                  }))
                }
              />
            </label>
            <label>
              Delivery photo <small>JPG, PNG, or WebP. Maximum 5 MB.</small>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) =>
                  setProof((current) => ({
                    ...current,
                    proofImage: event.target.files?.[0] || null,
                  }))
                }
                required
              />
            </label>
            <div>
              <button className="button button-primary">
                Confirm delivery
              </button>
              <button
                className="button button-dark"
                type="button"
                onClick={() =>
                  setProof({
                    requestNumber: "",
                    recipientName: "",
                    confirmationReference: "",
                    deliveryNotes: "",
                    proofImage: null,
                  })
                }
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}
      <div className="driver-finance-grid">
        <section className="driver-panel">
          <div className="operations-title">
            <Wallet size={23} />
            <h3>Withdrawal settings</h3>
          </div>
          <p className="security-note">
            For security, store only the payout destination label and the last
            four digits. Never enter a complete bank account number here.
          </p>
          <form onSubmit={saveSettings}>
            <label>
              Payout method
              <select
                value={settings.payoutMethod}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    payoutMethod: event.target.value,
                  }))
                }
              >
                <option value="bank_transfer">Bank transfer</option>
                <option value="check">Company check</option>
                <option value="cash_app">Cash App</option>
              </select>
            </label>
            <label>
              Destination label
              <input
                value={settings.destinationLabel}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    destinationLabel: event.target.value,
                  }))
                }
                placeholder="Example: Chase checking or $DriverName"
              />
            </label>
            <label>
              Account last four digits
              <input
                inputMode="numeric"
                maxLength="4"
                value={settings.accountLast4}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    accountLast4: event.target.value.replace(/\D/g, ""),
                  }))
                }
              />
            </label>
            <label>
              Payout schedule
              <select
                value={settings.payoutSchedule}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    payoutSchedule: event.target.value,
                  }))
                }
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Every two weeks</option>
                <option value="manual">Manual withdrawals</option>
              </select>
            </label>
            <label>
              Minimum payout
              <input
                type="number"
                min="10"
                max="1000"
                step="0.01"
                value={settings.minimumPayout}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    minimumPayout: Number(event.target.value),
                  }))
                }
              />
            </label>
            <label className="driver-checkbox">
              <input
                type="checkbox"
                checked={settings.withdrawalsEnabled}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    withdrawalsEnabled: event.target.checked,
                  }))
                }
              />{" "}
              Enable withdrawal requests
            </label>
            <button className="button button-dark">
              Save withdrawal settings
            </button>
          </form>
        </section>
        <section className="driver-panel">
          <div className="operations-title">
            <Banknote size={23} />
            <h3>Request withdrawal</h3>
          </div>
          <p>
            Available: <strong>{money(data.summary.available_balance)}</strong>
          </p>
          <form onSubmit={requestWithdrawal}>
            <label>
              Withdrawal amount
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={withdrawalAmount}
                onChange={(event) => setWithdrawalAmount(event.target.value)}
                required
              />
            </label>
            <button
              className="button button-primary"
              disabled={!settings.withdrawalsEnabled}
            >
              Request payout
            </button>
          </form>
          <div className="payout-list">
            <h4>Payout requests and receipts</h4>
            {!data.withdrawals.length ? (
              <p>No payout activity yet.</p>
            ) : (
              data.withdrawals.map((item) => (
                <article key={item.id}>
                  <div>
                    <strong>{money(item.amount)}</strong>
                    <span>{item.status}</span>
                  </div>
                  <small>{new Date(item.requested_at).toLocaleString()}</small>
                  <small>
                    {item.payout_method.replaceAll("_", " ")} ·{" "}
                    {item.destination_label || "Destination on file"}
                  </small>
                  {item.receipt_number && <b>Receipt: {item.receipt_number}</b>}
                </article>
              ))
            )}
          </div>
        </section>
      </div>
      <section className="driver-panel">
        <div className="operations-title">
          <History size={23} />
          <div>
            <h3>Order and ride history</h3>
            <span>{data.orderHistory.length} records</span>
          </div>
        </div>
        <div className="operations-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>Route</th>
                <th>Status</th>
                <th>Delivery proof</th>
                <th>Driver payment</th>
              </tr>
            </thead>
            <tbody>
              {data.orderHistory.map((order) => (
                <tr key={order.request_number}>
                  <td>
                    <strong>{order.request_number}</strong>
                    <span>{order.tracking_number}</span>
                  </td>
                  <td>
                    <span>
                      {order.pickup_city}, {order.pickup_state} →{" "}
                      {order.delivery_city}, {order.delivery_state}
                    </span>
                    <small>{order.service_type}</small>
                  </td>
                  <td>
                    <strong>{order.status}</strong>
                    <small>
                      {order.delivered_at
                        ? new Date(order.delivered_at).toLocaleString()
                        : ""}
                    </small>
                  </td>
                  <td>
                    <span>{order.proof_recipient || "Not recorded"}</span>
                    <small>{order.confirmation_reference}</small>
                  </td>
                  <td>
                    <strong>{order.earning_status || "Pending"}</strong>
                    <span>{money(order.driver_pay)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function ApplicationAdministration() {
  const [data, setData] = useState({
    applications: [],
    statuses: [],
    loading: true,
    error: "",
    notice: "",
    filter: "active",
  });
  async function loadApplications() {
    try {
      const result = await apiRequest("/applications/admin");
      setData((current) => ({
        ...current,
        ...result,
        loading: false,
        error: "",
      }));
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  useEffect(() => {
    loadApplications();
  }, []);
  async function saveReview(event, application) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setData((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
    }));
    try {
      await apiRequest(`/applications/admin/${application.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: form.get("status"),
          notes: form.get("notes"),
        }),
      });
      await loadApplications();
      setData((current) => ({
        ...current,
        loading: false,
        notice: `${application.reference} was updated.`,
      }));
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  const visible = data.applications.filter(
    (item) =>
      data.filter === "all" ||
      (data.filter === "active"
        ? !["approved", "declined"].includes(item.status)
        : item.status === data.filter),
  );
  const label = (value) =>
    String(value || "")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  return (
    <section className="application-administration">
      <header>
        <div>
          <p className="eyebrow">Administrator recruitment controls</p>
          <h2>Employment and driver applications</h2>
          <p>
            Review applicants, document decisions, monitor email delivery, and
            maintain an auditable recruiting workflow.
          </p>
        </div>
        <div className="application-summary">
          <span>
            <strong>
              {
                data.applications.filter((item) => item.status === "received")
                  .length
              }
            </strong>{" "}
            New
          </span>
          <span>
            <strong>
              {
                data.applications.filter(
                  (item) => item.status === "under_review",
                ).length
              }
            </strong>{" "}
            Reviewing
          </span>
          <span>
            <strong>{data.applications.length}</strong> Total
          </span>
        </div>
      </header>
      {data.error && (
        <div className="portal-auth-error" role="alert">
          {data.error}
        </div>
      )}
      {data.notice && (
        <div className="portal-admin-notice">
          <Check size={18} />
          <span>{data.notice}</span>
        </div>
      )}
      <div className="application-filters">
        <button
          type="button"
          className={data.filter === "active" ? "active" : ""}
          onClick={() =>
            setData((current) => ({ ...current, filter: "active" }))
          }
        >
          Active review
        </button>
        {data.statuses.map((status) => (
          <button
            type="button"
            key={status}
            className={data.filter === status ? "active" : ""}
            onClick={() =>
              setData((current) => ({ ...current, filter: status }))
            }
          >
            {label(status)}
          </button>
        ))}
        <button
          type="button"
          className={data.filter === "all" ? "active" : ""}
          onClick={() => setData((current) => ({ ...current, filter: "all" }))}
        >
          All
        </button>
      </div>
      {data.loading && !data.applications.length ? (
        <p>Loading applications…</p>
      ) : !visible.length ? (
        <p className="empty-requests">No applications match this view.</p>
      ) : (
        <div className="application-review-grid">
          {visible.map((application) => (
            <article key={application.id}>
              <div className="application-review-heading">
                <span
                  className={`application-type ${application.application_type}`}
                >
                  <BriefcaseBusiness size={16} />
                  {application.application_type === "driver"
                    ? "Driver"
                    : "Employee"}
                </span>
                <span className={`application-status ${application.status}`}>
                  {label(application.status)}
                </span>
              </div>
              <h3>
                {application.first_name} {application.last_name}
              </h3>
              <strong>{application.role}</strong>
              <div className="application-contact">
                <a href={`mailto:${application.email}`}>
                  <Mail size={15} />
                  {application.email}
                </a>
                <a href={`tel:${application.phone}`}>{application.phone}</a>
              </div>
              <dl>
                <div>
                  <dt>Reference</dt>
                  <dd>{application.reference}</dd>
                </div>
                <div>
                  <dt>Market</dt>
                  <dd>{application.market}</dd>
                </div>
                <div>
                  <dt>City</dt>
                  <dd>{application.city}</dd>
                </div>
                {application.vehicle && (
                  <div>
                    <dt>Vehicle</dt>
                    <dd>{application.vehicle}</dd>
                  </div>
                )}
                <div>
                  <dt>Submitted</dt>
                  <dd>{new Date(application.created_at).toLocaleString()}</dd>
                </div>
                <div>
                  <dt>Email delivery</dt>
                  <dd>
                    {application.recruitment_email_sent
                      ? "Recruitment notified"
                      : "Stored, notification pending"}{" "}
                    ·{" "}
                    {application.applicant_email_sent
                      ? "Applicant confirmed"
                      : "Applicant email pending"}
                  </dd>
                </div>
              </dl>
              <details>
                <summary>Qualifications and availability</summary>
                <p>{application.experience}</p>
                <p>
                  <strong>Availability:</strong> {application.availability}
                </p>
                {application.resume_url && (
                  <a
                    href={application.resume_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open resume or professional profile
                  </a>
                )}
              </details>
              <form onSubmit={(event) => saveReview(event, application)}>
                <label>
                  Review status
                  <select name="status" defaultValue={application.status}>
                    {data.statuses.map((status) => (
                      <option key={status} value={status}>
                        {label(status)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Administrator notes
                  <textarea
                    name="notes"
                    defaultValue={application.review_notes || ""}
                    maxLength="2000"
                    placeholder="Document screening, contact attempts, verification needs, or decision reasons."
                  />
                </label>
                <button className="button button-dark" disabled={data.loading}>
                  Save review
                </button>
              </form>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function PayoutAdministration() {
  const [data, setData] = useState({
    orders: [],
    withdrawals: [],
    audit: [],
    loading: true,
    error: "",
    notice: "",
  });
  async function loadPayouts() {
    try {
      const result = await apiRequest("/admin-payouts/dashboard");
      setData((current) => ({
        ...current,
        ...result,
        loading: false,
        error: "",
      }));
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  useEffect(() => {
    loadPayouts();
  }, []);
  async function saveEarning(event, order) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await apiRequest(
        `/admin-payouts/earnings/${encodeURIComponent(order.request_number)}`,
        {
          method: "PUT",
          body: JSON.stringify({
            grossAmount: Number(form.get("grossAmount")),
            adjustmentAmount: Number(form.get("adjustmentAmount")),
            details: form.get("details"),
          }),
        },
      );
      await loadPayouts();
      setData((current) => ({
        ...current,
        notice: `Driver earnings saved for ${order.request_number}.`,
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function updateWithdrawal(event, withdrawal) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const result = await apiRequest(
        `/admin-payouts/withdrawals/${withdrawal.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status: form.get("status"),
            notes: form.get("notes"),
          }),
        },
      );
      await loadPayouts();
      setData((current) => ({
        ...current,
        notice: result.receiptNumber
          ? `Payout completed. Receipt ${result.receiptNumber} was generated.`
          : "Payout request updated.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  function exportAccounting() {
    const rows = [
      [
        "Record Type",
        "Order or Receipt",
        "Driver",
        "Email",
        "Gross or Amount",
        "Adjustment",
        "Net or Status",
        "Date",
      ],
    ];
    data.orders.forEach((item) =>
      rows.push([
        "Driver earning",
        item.request_number,
        item.driver_name,
        item.driver_email,
        item.gross_amount || 0,
        item.adjustment_amount || 0,
        item.net_amount || 0,
        item.earned_at || item.assigned_at || "",
      ]),
    );
    data.withdrawals.forEach((item) =>
      rows.push([
        "Payout",
        item.receipt_number || `Withdrawal ${item.id}`,
        item.driver_name,
        item.driver_email,
        item.amount,
        "",
        item.status,
        item.processed_at || item.requested_at || "",
      ]),
    );
    const csv = rows
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replaceAll('"', '""')}"`)
          .join(","),
      )
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `just-go-driver-payments-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }
  if (data.loading)
    return (
      <section className="payout-admin">
        <p>Loading driver payment administration…</p>
      </section>
    );
  return (
    <section className="payout-admin">
      <header>
        <div>
          <p className="eyebrow">Administrator financial controls</p>
          <h2>Driver payments and payouts</h2>
          <p>
            Set order compensation, review withdrawal requests, generate payout
            receipts, and maintain accountable payment records.
          </p>
        </div>
        <button
          className="button button-dark"
          type="button"
          onClick={exportAccounting}
        >
          <Download size={17} /> Export accounting CSV
        </button>
      </header>
      {data.error && (
        <div className="portal-auth-error" role="alert">
          {data.error}
        </div>
      )}
      {data.notice && (
        <div className="portal-admin-notice">
          <Check size={18} />
          <span>{data.notice}</span>
        </div>
      )}
      <section className="payout-section">
        <div className="operations-title">
          <Banknote size={23} />
          <div>
            <h3>Order compensation</h3>
            <span>{data.orders.length} assigned orders</span>
          </div>
        </div>
        <div className="payout-order-grid">
          {data.orders.map((order) => (
            <article key={order.request_number}>
              <div>
                <strong>{order.request_number}</strong>
                <span>
                  {order.driver_name} · {order.driver_email}
                </span>
                <small>
                  {order.pickup_city}, {order.pickup_state} →{" "}
                  {order.delivery_city}, {order.delivery_state}
                </small>
                <small>
                  Order: {order.order_status} · Customer payment: $
                  {Number(order.amount_paid || 0).toFixed(2)}
                </small>
              </div>
              <form onSubmit={(event) => saveEarning(event, order)}>
                <label>
                  Gross driver pay
                  <input
                    name="grossAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={Number(order.gross_amount || 0).toFixed(2)}
                    required
                  />
                </label>
                <label>
                  Adjustment
                  <input
                    name="adjustmentAmount"
                    type="number"
                    step="0.01"
                    defaultValue={Number(order.adjustment_amount || 0).toFixed(
                      2,
                    )}
                  />
                </label>
                <label>
                  Payment note
                  <input
                    name="details"
                    placeholder="Reason, mileage, bonus, or deduction"
                  />
                </label>
                <div>
                  <span>
                    Net: ${Number(order.net_amount || 0).toFixed(2)} ·{" "}
                    {order.earning_status || "Not calculated"}
                  </span>
                  <button className="button button-dark">
                    Save compensation
                  </button>
                </div>
              </form>
            </article>
          ))}
        </div>
      </section>
      <section className="payout-section">
        <div className="operations-title">
          <Wallet size={23} />
          <div>
            <h3>Withdrawal requests and receipts</h3>
            <span>{data.withdrawals.length} records</span>
          </div>
        </div>
        {!data.withdrawals.length ? (
          <p>No driver withdrawal requests have been submitted.</p>
        ) : (
          <div className="payout-request-grid">
            {data.withdrawals.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.driver_name}</strong>
                  <span>{item.driver_email}</span>
                  <b>
                    ${Number(item.amount).toFixed(2)} · {item.status}
                  </b>
                  <small>
                    {item.payout_method.replaceAll("_", " ")} ·{" "}
                    {item.destination_label || "Destination on file"}
                  </small>
                  <small>
                    Requested {new Date(item.requested_at).toLocaleString()}
                  </small>
                  {item.receipt_number && (
                    <em>Receipt: {item.receipt_number}</em>
                  )}
                </div>
                <form onSubmit={(event) => updateWithdrawal(event, item)}>
                  <label>
                    Status
                    <select
                      name="status"
                      defaultValue={
                        item.status === "REQUESTED" ? "APPROVED" : item.status
                      }
                    >
                      <option value="APPROVED">Approved</option>
                      <option value="PROCESSING">Processing</option>
                      <option value="PAID">Paid</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </label>
                  <label>
                    Administrator notes
                    <textarea name="notes" defaultValue={item.notes || ""} />
                  </label>
                  <button className="button button-primary">
                    Update payout
                  </button>
                </form>
              </article>
            ))}
          </div>
        )}
      </section>
      <section className="payout-section">
        <div className="operations-title">
          <History size={23} />
          <div>
            <h3>Payment audit history</h3>
            <span>{data.audit.length} actions</span>
          </div>
        </div>
        <div className="audit-list">
          {data.audit.map((item) => (
            <article key={item.id}>
              <strong>{item.action.replaceAll("_", " ")}</strong>
              <span>
                {item.driver_name} ·{" "}
                {item.request_number || `Withdrawal ${item.withdrawal_id}`}
              </span>
              <small>
                {item.administrator_name} ·{" "}
                {new Date(item.created_at).toLocaleString()}
              </small>
              <p>{item.details}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function CustomerWorkspace() {
  const emptyLocation = {
    label: "",
    companyName: "",
    street: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    contactName: "",
    contactPhone: "",
  };
  const [data, setData] = useState({
    activeOrders: [],
    orderHistory: [],
    locations: [],
    supportRequests: [],
    loading: true,
    error: "",
    notice: "",
  });
  const [location, setLocation] = useState(emptyLocation);
  const [support, setSupport] = useState({
    requestNumber: "",
    subject: "",
    message: "",
  });
  async function loadCustomer() {
    try {
      const result = await apiRequest("/customer/dashboard");
      setData((current) => ({
        ...current,
        ...result,
        loading: false,
        error: "",
      }));
    } catch (error) {
      setData((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  useEffect(() => {
    loadCustomer();
  }, []);
  async function saveLocation(event) {
    event.preventDefault();
    try {
      await apiRequest("/customer/locations", {
        method: "POST",
        body: JSON.stringify(location),
      });
      setLocation(emptyLocation);
      await loadCustomer();
      setData((current) => ({
        ...current,
        notice: "Location saved to your account.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function removeLocation(id) {
    try {
      await apiRequest(`/customer/locations/${id}`, { method: "DELETE" });
      await loadCustomer();
      setData((current) => ({ ...current, notice: "Saved location removed." }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  async function sendSupport(event) {
    event.preventDefault();
    try {
      await apiRequest("/customer/support", {
        method: "POST",
        body: JSON.stringify(support),
      });
      setSupport({ requestNumber: "", subject: "", message: "" });
      await loadCustomer();
      setData((current) => ({
        ...current,
        notice: "Your support request was submitted.",
      }));
    } catch (error) {
      setData((current) => ({ ...current, error: error.message }));
    }
  }
  const money = (value) => `$${Number(value || 0).toFixed(2)}`;
  if (data.loading)
    return (
      <section className="customer-workspace">
        <p>Loading customer dashboard…</p>
      </section>
    );
  const orderCard = (order) => (
    <article key={order.request_number}>
      <div className="customer-order-head">
        <div>
          <strong>{order.request_number}</strong>
          <span>{order.status.replaceAll("_", " ")}</span>
        </div>
        <small>{order.tracking_number || "Tracking pending"}</small>
      </div>
      <div className="customer-route">
        <p>
          <b>Pickup</b>
          {order.pickup_company}
          <br />
          {order.pickup_city}, {order.pickup_state}
          <br />
          {order.pickup_date} · {order.pickup_time}
        </p>
        <ArrowRight />
        <p>
          <b>Delivery</b>
          {order.delivery_company}
          <br />
          {order.delivery_city}, {order.delivery_state}
          <br />
          {order.delivery_date} · {order.delivery_time}
        </p>
      </div>
      <div className="customer-order-details">
        <span>Service: {order.service_type}</span>
        <span>
          Payment: {order.payment_status} ·{" "}
          {money(order.amount_paid || order.estimated_price)}
        </span>
        <span>Driver: {order.driver_name || "Not assigned"}</span>
        <span>
          Estimated delivery:{" "}
          {order.estimated_delivery_eta || "Pending confirmation"}
        </span>
        {order.delivered_at && (
          <span>
            Delivered: {new Date(order.delivered_at).toLocaleString()} to{" "}
            {order.proof_recipient}
          </span>
        )}
        {order.confirmation_reference && (
          <span>Delivery confirmation: {order.confirmation_reference}</span>
        )}
      </div>
      {order.payment_status === "PAID" && (
        <div className="customer-receipt">
          <Receipt size={18} />
          <span>
            <b>Payment receipt</b>
            {money(order.amount_paid)} paid{" "}
            {order.paid_at ? new Date(order.paid_at).toLocaleString() : ""}
            <small>{order.stripe_payment_intent_id}</small>
          </span>
        </div>
      )}
    </article>
  );
  return (
    <section className="customer-workspace">
      <header>
        <p className="eyebrow">Customer account</p>
        <h2>Deliveries, tracking and receipts</h2>
        <p>
          Review your active deliveries, payment records, completed orders,
          saved locations, and customer-support requests.
        </p>
      </header>
      {data.error && (
        <div className="portal-auth-error" role="alert">
          {data.error}
        </div>
      )}
      {data.notice && (
        <div className="portal-admin-notice">
          <Check size={18} />
          <span>{data.notice}</span>
        </div>
      )}
      <div className="customer-summary">
        <article>
          <PackageSearch />
          <span>Active deliveries</span>
          <strong>{data.activeOrders.length}</strong>
        </article>
        <article>
          <PackageCheck />
          <span>Completed</span>
          <strong>
            {
              data.orderHistory.filter((item) => item.status === "DELIVERED")
                .length
            }
          </strong>
        </article>
        <article>
          <MapPinned />
          <span>Saved locations</span>
          <strong>{data.locations.length}</strong>
        </article>
      </div>
      <section className="customer-panel">
        <div className="customer-section-heading">
          <div className="operations-title">
            <Truck size={23} />
            <h3>Active deliveries</h3>
          </div>
          <a className="button button-primary" href="/request-delivery">
            Request a delivery <ArrowRight size={16} />
          </a>
        </div>
        {!data.activeOrders.length ? (
          <p>No active deliveries are associated with this account.</p>
        ) : (
          <div className="customer-order-grid">
            {data.activeOrders.map(orderCard)}
          </div>
        )}
      </section>
      <section className="customer-panel">
        <div className="operations-title">
          <History size={23} />
          <h3>Order and delivery history</h3>
        </div>
        {!data.orderHistory.length ? (
          <p>No completed delivery history yet.</p>
        ) : (
          <div className="customer-order-grid">
            {data.orderHistory.map(orderCard)}
          </div>
        )}
      </section>
      <div className="customer-tools">
        <section className="customer-panel">
          <div className="operations-title">
            <MapPinned size={23} />
            <h3>Saved locations</h3>
          </div>
          <div className="saved-location-list">
            {data.locations.map((item) => (
              <article key={item.id}>
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.company_name}</span>
                  <small>
                    {item.street}
                    {item.address_line_2
                      ? `, ${item.address_line_2}`
                      : ""}, {item.city}, {item.state} {item.zip}
                  </small>
                </div>
                <button
                  type="button"
                  aria-label={`Remove ${item.label}`}
                  onClick={() => removeLocation(item.id)}
                >
                  <Trash2 size={16} />
                </button>
              </article>
            ))}
          </div>
          <form onSubmit={saveLocation}>
            {Object.entries({
              label: "Location label",
              companyName: "Company name",
              street: "Street address",
              addressLine2: "Address line 2",
              city: "City",
              state: "State",
              zip: "ZIP code",
              contactName: "Contact name",
              contactPhone: "Contact phone",
            }).map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  value={location[key]}
                  onChange={(event) =>
                    setLocation((current) => ({
                      ...current,
                      [key]: event.target.value,
                    }))
                  }
                  required={[
                    "label",
                    "street",
                    "city",
                    "state",
                    "zip",
                  ].includes(key)}
                />
              </label>
            ))}
            <button className="button button-dark">Save location</button>
          </form>
        </section>
        <section className="customer-panel">
          <div className="operations-title">
            <Headphones size={23} />
            <h3>Customer support</h3>
          </div>
          <form onSubmit={sendSupport}>
            <label>
              Related order <small>Optional</small>
              <select
                value={support.requestNumber}
                onChange={(event) =>
                  setSupport((current) => ({
                    ...current,
                    requestNumber: event.target.value,
                  }))
                }
              >
                <option value="">General support</option>
                {[...data.activeOrders, ...data.orderHistory].map((item) => (
                  <option key={item.request_number}>
                    {item.request_number}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Subject
              <input
                value={support.subject}
                onChange={(event) =>
                  setSupport((current) => ({
                    ...current,
                    subject: event.target.value,
                  }))
                }
                required
              />
            </label>
            <label>
              Message
              <textarea
                value={support.message}
                onChange={(event) =>
                  setSupport((current) => ({
                    ...current,
                    message: event.target.value,
                  }))
                }
                required
              />
            </label>
            <button className="button button-primary">
              Submit support request
            </button>
          </form>
          <div className="support-history">
            <h4>Previous requests</h4>
            {data.supportRequests.map((item) => (
              <article key={item.id}>
                <strong>{item.subject}</strong>
                <span>
                  {item.status} · {new Date(item.created_at).toLocaleString()}
                </span>
                <p>{item.message}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </section>
  );
}

export function PortalDashboardPage() {
  const [state, setState] = useState({ loading: true, user: null, error: "" });
  const [directory, setDirectory] = useState({
    users: [],
    roles: [],
    loading: false,
    error: "",
    notice: "",
    temporaryPassword: "",
  });
  const [accessRequests, setAccessRequests] = useState([]);
  const [newUser, setNewUser] = useState({
    fullName: "",
    email: "",
    role: "employee",
  });
  useEffect(() => {
    apiRequest("/auth/me")
      .then(({ user }) => setState({ loading: false, user, error: "" }))
      .catch(() => window.location.assign("/sign-in"));
  }, []);
  useEffect(() => {
    if (state.user?.role === "administrator") {
      loadUsers();
      loadAccessRequests();
    }
  }, [state.user?.role]);
  setPageMeta({
    title: "Account Dashboard | Just Go Logistics",
    description: "Secure Just Go Logistics portal dashboard.",
    path: "/portal",
  });
  async function logout() {
    await apiRequest("/auth/logout", { method: "POST" });
    window.location.assign("/sign-in");
  }
  async function loadUsers() {
    setDirectory((current) => ({ ...current, loading: true, error: "" }));
    try {
      const result = await apiRequest("/auth/admin/users");
      setDirectory((current) => ({
        ...current,
        users: result.users,
        roles: result.roles,
        loading: false,
      }));
    } catch (error) {
      setDirectory((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function loadAccessRequests() {
    try {
      const result = await apiRequest("/auth/admin/access-requests");
      setAccessRequests(result.requests);
    } catch (error) {
      setDirectory((current) => ({ ...current, error: error.message }));
    }
  }
  async function createUser(event) {
    event.preventDefault();
    setDirectory((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
      temporaryPassword: "",
    }));
    try {
      const result = await apiRequest("/auth/admin/users", {
        method: "POST",
        body: JSON.stringify(newUser),
      });
      setNewUser({ fullName: "", email: "", role: "employee" });
      setDirectory((current) => ({
        ...current,
        loading: false,
        notice: `Account created for ${result.user.full_name}. Copy the temporary password now.`,
        temporaryPassword: result.temporaryPassword,
      }));
      await loadUsers();
    } catch (error) {
      setDirectory((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function updateUser(user, changes) {
    setDirectory((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
      temporaryPassword: "",
    }));
    try {
      await apiRequest(`/auth/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          fullName: user.full_name,
          role: user.role,
          isActive: Boolean(user.is_active),
          ...changes,
        }),
      });
      setDirectory((current) => ({
        ...current,
        loading: false,
        notice: "Account permissions updated.",
      }));
      await loadUsers();
    } catch (error) {
      setDirectory((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function resetPassword(user) {
    if (
      !window.confirm(
        `Issue a new temporary password for ${user.full_name}? Their current password will stop working.`,
      )
    )
      return;
    setDirectory((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
      temporaryPassword: "",
    }));
    try {
      const result = await apiRequest(
        `/auth/admin/users/${user.id}/reset-password`,
        { method: "POST" },
      );
      setDirectory((current) => ({
        ...current,
        loading: false,
        notice: `Temporary password created for ${user.full_name}. Copy it now.`,
        temporaryPassword: result.temporaryPassword,
      }));
    } catch (error) {
      setDirectory((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function reviewAccessRequest(request, decision) {
    if (
      decision === "reject" &&
      !window.confirm(`Reject the access request from ${request.full_name}?`)
    )
      return;
    setDirectory((current) => ({
      ...current,
      loading: true,
      error: "",
      notice: "",
      temporaryPassword: "",
    }));
    try {
      const result = await apiRequest(
        `/auth/admin/access-requests/${request.id}/${decision}`,
        { method: "POST" },
      );
      if (decision === "approve")
        setDirectory((current) => ({
          ...current,
          loading: false,
          notice: `${request.full_name} was approved. Copy the temporary password now.`,
          temporaryPassword: result.temporaryPassword,
        }));
      else
        setDirectory((current) => ({
          ...current,
          loading: false,
          notice: `${request.full_name}’s request was rejected.`,
        }));
      await Promise.all([loadUsers(), loadAccessRequests()]);
    } catch (error) {
      setDirectory((current) => ({
        ...current,
        loading: false,
        error: error.message,
      }));
    }
  }
  async function copyTemporaryPassword() {
    await navigator.clipboard.writeText(directory.temporaryPassword);
    setDirectory((current) => ({
      ...current,
      notice: "Temporary password copied. Share it through a secure channel.",
    }));
  }
  if (state.loading)
    return (
      <Shell>
        <section className="portal-auth-page">
          <div className="portal-auth-card">
            <p>Loading your secure workspace…</p>
          </div>
        </section>
      </Shell>
    );
  const isPrimaryAdministrator =
    state.user?.role === "administrator" &&
    state.user?.email?.toLowerCase() === "info@2kstechnologies.com";
  return (
    <Shell>
      <section className="portal-dashboard">
        <header>
          <div>
            <p className="eyebrow">Secure workspace</p>
            <h1>Welcome, {state.user?.name}</h1>
            <p>
              {isPrimaryAdministrator
                ? "Primary administrator account"
                : `${state.user?.role} account`}
            </p>
          </div>
          <button className="button button-dark" onClick={logout}>
            <LogOut size={17} /> Logout
          </button>
        </header>
        <div className="portal-dashboard-grid">
          <article>
            <UserRound size={25} />
            <h2>My account</h2>
            <p>{state.user?.email}</p>
            <span>Role: {state.user?.role}</span>
          </article>
          <article>
            <ShieldCheck size={25} />
            <h2>Authorized access</h2>
            <p>
              Your dashboard displays only the tools and records permitted for
              your assigned role.
            </p>
          </article>
          {isPrimaryAdministrator && (
            <article>
              <UsersRound size={25} />
              <h2>Account administration</h2>
              <p>
                Create accounts, approve access requests, assign job roles,
                suspend access, and issue password resets.
              </p>
            </article>
          )}
        </div>
        {["administrator", "employee", "dispatcher"].includes(
          state.user?.role,
        ) && <OperationsWorkspace />}
        {state.user?.role === "driver" && <DriverWorkspace />}
        {state.user?.role === "customer" && <CustomerWorkspace />}
        {isPrimaryAdministrator && <ApplicationAdministration />}
        {isPrimaryAdministrator && <PayoutAdministration />}
        {isPrimaryAdministrator && (
          <section className="account-administration">
            <div className="account-admin-heading">
              <div>
                <p className="eyebrow">Administrator controls</p>
                <h2>User accounts and roles</h2>
                <p>
                  Only approve verified personnel and customers. Every approved
                  user must replace the temporary password at first login.
                </p>
              </div>
            </div>
            {directory.error && (
              <div className="portal-auth-error" role="alert">
                {directory.error}
              </div>
            )}
            {directory.notice && (
              <div className="portal-admin-notice">
                <Check size={18} />
                <span>{directory.notice}</span>
              </div>
            )}
            {directory.temporaryPassword && (
              <div className="temporary-password">
                <div>
                  <span>One-time temporary password</span>
                  <strong>{directory.temporaryPassword}</strong>
                </div>
                <button type="button" onClick={copyTemporaryPassword}>
                  <Copy size={17} /> Copy password
                </button>
              </div>
            )}
            <div className="access-request-list">
              <div className="user-directory-header">
                <h3>Access requests</h3>
                <span>
                  {
                    accessRequests.filter(
                      (request) => request.status === "pending",
                    ).length
                  }{" "}
                  pending
                </span>
              </div>
              {accessRequests.filter((request) => request.status === "pending")
                .length === 0 ? (
                <p className="empty-requests">No pending access requests.</p>
              ) : (
                accessRequests
                  .filter((request) => request.status === "pending")
                  .map((request) => (
                    <article key={request.id}>
                      <div>
                        <strong>{request.full_name}</strong>
                        <span>
                          {request.email}
                          {request.phone ? ` · ${request.phone}` : ""}
                        </span>
                        <small>
                          {request.requested_role} access · Requested{" "}
                          {new Date(request.created_at).toLocaleDateString()}
                        </small>
                        {request.organization && (
                          <p>Organization: {request.organization}</p>
                        )}
                        {request.request_message && (
                          <p>{request.request_message}</p>
                        )}
                      </div>
                      <div>
                        <button
                          className="approve-request"
                          type="button"
                          onClick={() =>
                            reviewAccessRequest(request, "approve")
                          }
                          disabled={directory.loading}
                        >
                          Approve
                        </button>
                        <button
                          className="reject-request"
                          type="button"
                          onClick={() => reviewAccessRequest(request, "reject")}
                          disabled={directory.loading}
                        >
                          Reject
                        </button>
                      </div>
                    </article>
                  ))
              )}
            </div>
            <form className="create-account-form" onSubmit={createUser}>
              <div className="form-title">
                <Plus size={21} />
                <h3>Create an approved account directly</h3>
              </div>
              <label>
                Full name
                <input
                  value={newUser.fullName}
                  onChange={(event) =>
                    setNewUser((current) => ({
                      ...current,
                      fullName: event.target.value,
                    }))
                  }
                  required
                  maxLength="160"
                />
              </label>
              <label>
                Email address
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(event) =>
                    setNewUser((current) => ({
                      ...current,
                      email: event.target.value,
                    }))
                  }
                  required
                />
              </label>
              <label>
                Assigned role
                <select
                  value={newUser.role}
                  onChange={(event) =>
                    setNewUser((current) => ({
                      ...current,
                      role: event.target.value,
                    }))
                  }
                >
                  {directory.roles.map((role) => (
                    <option key={role} value={role}>
                      {role.replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="button button-primary"
                disabled={directory.loading}
              >
                {directory.loading ? "Processing…" : "Create account"}{" "}
                <ArrowRight size={17} />
              </button>
            </form>
            <div className="user-directory">
              <div className="user-directory-header">
                <h3>Existing accounts</h3>
                <span>
                  {directory.users.length} account
                  {directory.users.length === 1 ? "" : "s"}
                </span>
              </div>
              {directory.loading && !directory.users.length ? (
                <p>Loading accounts…</p>
              ) : (
                <div className="user-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Password</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {directory.users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <strong>{user.full_name}</strong>
                            <span>{user.email}</span>
                          </td>
                          <td>
                            <select
                              aria-label={`Role for ${user.full_name}`}
                              value={user.role}
                              onChange={(event) =>
                                updateUser(user, { role: event.target.value })
                              }
                              disabled={directory.loading}
                            >
                              {directory.roles.map((role) => (
                                <option key={role} value={role}>
                                  {role.replaceAll("_", " ")}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <button
                              className={`status-toggle ${user.is_active ? "active" : "suspended"}`}
                              type="button"
                              onClick={() =>
                                updateUser(user, { isActive: !user.is_active })
                              }
                              disabled={
                                directory.loading ||
                                Number(user.id) === Number(state.user?.id)
                              }
                            >
                              {user.is_active ? "Active" : "Suspended"}
                            </button>
                          </td>
                          <td>
                            <span
                              className={
                                user.must_change_password
                                  ? "password-pending"
                                  : "password-current"
                              }
                            >
                              {user.must_change_password
                                ? "Reset required"
                                : "Current"}
                            </span>
                          </td>
                          <td>
                            <button
                              className="table-action"
                              type="button"
                              onClick={() => resetPassword(user)}
                              disabled={directory.loading}
                            >
                              <KeyRound size={15} /> Reset password
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}
      </section>
    </Shell>
  );
}
